/**
 * 刊登任务列表：批次进度、预览与详情行日志。
 */

export const LISTING_JOB_QUERY_KEY = 'listingJobId';
export const SCHEDULED_LISTING_QUERY_KEY = 'scheduledListingId';

export function readScheduledListingIdFromSearch(search) {
  return String(new URLSearchParams(search || '').get(SCHEDULED_LISTING_QUERY_KEY) || '').trim();
}

/** 展示批次号：新任务用 batchNo，历史任务回退 id */
export function resolveListingJobBatchNo(job, fallbackId = '') {
  const batchNo = String(job?.batchNo || '').trim();
  if (batchNo) return batchNo;
  const id = job?.id ?? fallbackId;
  if (id == null || id === '') return '';
  return String(id);
}

export function readListingJobIdFromSearch(search) {
  return String(new URLSearchParams(search || '').get(LISTING_JOB_QUERY_KEY) || '').trim();
}

export function listingJobProcessedCount(job) {
  const listed = Number(job?.listedCount) || 0;
  const failed = Number(job?.failedCount) || 0;
  return listed + failed;
}

export function listingJobLineCount(job) {
  const total = Number(job?.lineCount);
  if (Number.isFinite(total) && total > 0) return total;
  const listed = Number(job?.listedCount) || 0;
  const failed = Number(job?.failedCount) || 0;
  const pending = Number(job?.pendingCount) || 0;
  return listed + failed + pending;
}

/** 推送平台进度：转换中按已转换条数，否则按已处理行 / 总行数。 */
export function listingJobProgressPercent(job) {
  const status = String(job?.status || '').toLowerCase();
  if (status === 'preparing') {
    const prepareTotal = Number(job?.prepareTotal) || 0;
    const prepareDone = Number(job?.prepareDone) || 0;
    if (prepareTotal > 0) {
      const raw = Math.round((prepareDone / prepareTotal) * 100);
      // 条数已满但仍停在 preparing：展示 99%，避免「100% 还在转换中」误导
      if (raw >= 100) return 99;
      return Math.max(0, Math.min(99, raw));
    }
  }
  const total = listingJobLineCount(job);
  if (total <= 0) {
    if (status === 'done' || status === 'partial' || status === 'failed' || status === 'cancelled') {
      return 100;
    }
    return 0;
  }
  return Math.min(100, Math.round((listingJobProcessedCount(job) / total) * 100));
}

export function listingJobPreviewUrls(job) {
  const list = Array.isArray(job?.previewImageUrls)
    ? job.previewImageUrls
    : (Array.isArray(job?.preview_image_urls) ? job.preview_image_urls : []);
  return uniqueStrings(list).slice(0, 4);
}

function uniqueStrings(list) {
  const out = [];
  (Array.isArray(list) ? list : []).forEach((raw) => {
    const value = String(raw || '').trim();
    if (!value || out.includes(value)) return;
    out.push(value);
  });
  return out;
}

export function listingJobLinePlatformSpu(line) {
  return String(line?.platformProductId || line?.platformSpu || '').trim();
}

export function listingJobLinePlatformSkcs(line) {
  const fromField = line?.platformSkcIds ?? line?.platform_skc_ids;
  if (Array.isArray(fromField)) return uniqueStrings(fromField);
  if (typeof fromField === 'string' && fromField.trim()) {
    return uniqueStrings(fromField.split(/[,，;；\s]+/));
  }
  return [];
}

/**
 * 详情「日志」：成功展示平台返回的 SPU / SKC，失败展示失败原因。
 */
export function listingJobLineLogView(line) {
  const status = String(line?.lineStatus || line?.line_status || '').toLowerCase();
  const errorMessage = String(line?.errorMessage || line?.error_message || '').trim();
  if (status === 'failed') {
    return {
      kind: 'failed',
      message: errorMessage || '刊登失败',
      spu: '',
      skcs: [],
    };
  }
  if (status === 'preparing') {
    // 日志已写明校验/转换失败时，按失败展示，避免任务头仍停在「转换中」时行日志误导
    if (/校验失败|转换失败|不符合要求|请先删减/.test(errorMessage)) {
      return {
        kind: 'failed',
        message: errorMessage,
        spu: '',
        skcs: [],
      };
    }
    return {
      kind: 'progress',
      message: errorMessage || '正在转换图片和视频',
      spu: '',
      skcs: [],
    };
  }
  if (status !== 'listed') {
    return { kind: 'empty', message: '', spu: '', skcs: [] };
  }
  return {
    kind: 'listed',
    message: '',
    spu: listingJobLinePlatformSpu(line),
    skcs: listingJobLinePlatformSkcs(line),
  };
}

export function formatListingJobLineLog(line) {
  const view = listingJobLineLogView(line);
  if (view.kind === 'failed' || view.kind === 'progress') return view.message;
  if (view.kind !== 'listed') return '';
  const parts = [];
  if (view.spu) parts.push(`SPU：${view.spu}`);
  if (view.skcs.length) parts.push(`SKC：${view.skcs.join('、')}`);
  return parts.join('\n') || '平台已受理';
}

export function listingJobLineToListingItem(line) {
  if (!line) return null;
  const printImageUrls = uniqueStrings(line.printImageUrls || line.print_image_urls);
  const printImageNames = Array.isArray(line.printImageNames || line.print_image_names)
    ? (line.printImageNames || line.print_image_names).map((name) => String(name || '').trim())
    : [];
  const colorSlots = Array.isArray(line.colorSlots)
    ? line.colorSlots
    : (Array.isArray(line.color_slots) ? line.color_slots : []);
  const primary = String(line.primaryImageUrl || line.primary_image_url || '').trim();
  const slotMains = uniqueStrings(colorSlots.map((slot) => slot?.mainUrl || slot?.main_url));
  const preferredProductVideo = line.preferredProductVideo
    && typeof line.preferredProductVideo === 'object'
    ? line.preferredProductVideo
    : (line.preferred_product_video && typeof line.preferred_product_video === 'object'
      ? line.preferred_product_video
      : null);
  return {
    id: line.id,
    title: line.title || '',
    primaryImageUrl: primary,
    imageUrls: uniqueStrings([primary, ...slotMains]),
    printImageUrls,
    printImageNames,
    colorSlots,
    ...(preferredProductVideo ? { preferredProductVideo } : {}),
  };
}

/** 颜色列右侧「主图」：各 SKC 主图，缺省回退行主图。 */
export function listingJobLineMainImages(line, group) {
  const urls = [];
  (group?.skcRows || []).forEach((row) => {
    (row?.compositeImages || []).forEach((img) => {
      const url = String(img?.url || '').trim();
      if (url && !urls.includes(url)) urls.push(url);
    });
  });
  if (urls.length) return urls;
  const primary = String(line?.primaryImageUrl || line?.primary_image_url || '').trim();
  return primary ? [primary] : [];
}
